import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSession } from '@supabase/auth-helpers-react';
import { 
  getPublicTutorProfile, 
  getPublicSetting, 
  requestTutor, 
  getStudentTutorConnections,
  updateStudentTutorStatus 
} from '../api/supabaseAPI';
import Loader from '../components/Loader';
import Message from '../components/Message';
import '../styles/PublicTutorProfile.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

export default function PublicTutorProfile() {
  const { t } = useTranslation();
  const { uid, language } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const queryClient = useQueryClient();
  const currentUserId = session?.user?.id;
  const [requestMessage, setRequestMessage] = useState({ text: '', type: '' });

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const { data: tutor, isLoading, error } = useQuery({
    queryKey: ['publicTutorProfile', uid, language],
    queryFn: () => getPublicTutorProfile(uid, language),
    enabled: !!uid && !!language,
    retry: false
  });

  // Fetch connection status
  const { data: connections } = useQuery({
    queryKey: ['studentTutorConnections', currentUserId],
    queryFn: () => getStudentTutorConnections(currentUserId),
    enabled: !!currentUserId,
  });

  const connection = useMemo(() => {
    return connections?.find(c => c.tutor_id === uid && c.language === language);
  }, [connections, uid, language]);

  const { mutate: handleRequest, isPending: isRequesting } = useMutation({
    mutationFn: () => requestTutor(uid, currentUserId, language),
    onSuccess: () => {
      setRequestMessage({ text: 'Request sent successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['studentTutorConnections'] });
    },
    onError: (err) => {
      setRequestMessage({ text: err.message || 'Failed to send request.', type: 'error' });
    }
  });

  const { mutate: handleAccept, isPending: isAccepting } = useMutation({
    mutationFn: () => updateStudentTutorStatus(connection.relationship_id, 'accepted'),
    onSuccess: () => {
      setRequestMessage({ text: 'Invitation accepted!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['studentTutorConnections'] });
    },
    onError: (err) => {
      setRequestMessage({ text: err.message || 'Failed to accept.', type: 'error' });
    }
  });

  const getImageUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    return `${publicBaseUrl}/${path}`;
  };

  const avatarUrl = getImageUrl(tutor?.avatar) || '/Resources/imagine/default_avatar/default_avatar.jpeg';
  const backgroundUrl = getImageUrl(tutor?.background);

  const workHistory = useMemo(() => {
    if (!tutor?.work_history) return [];
    if (typeof tutor.work_history === 'string') {
        try {
            return JSON.parse(tutor.work_history);
        } catch {
            return [];
        }
    }
    return Array.isArray(tutor.work_history) ? tutor.work_history : [];
  }, [tutor?.work_history]);

  const handleBack = () => {
    const from = location.state?.from;
    if (from?.pathname) {
      navigate({
        pathname: from.pathname,
        search: from.search || '',
      });
      return;
    }
    navigate('/tutors');
  };

  if (isLoading) return <Loader />;
  if (error || !tutor) return (
    <div className="public-profile-wrapper">
        <Message text={t('publicProfile.notFound', 'Tutor not found or not active.')} type="error" />
        <button onClick={handleBack} className="btn-secondary" style={{marginTop: '1rem'}}>Back to List</button>
    </div>
  );

  // Determine Main Action Button
  let ActionButton = null;

  if (connection) {
    if (connection.status === 'accepted') {
        ActionButton = (
            <button className="action-btn btn-primary" disabled>
                Message (Coming Soon)
            </button>
        );
    } else if (connection.status === 'pending') {
        if (connection.action_by === 'student') {
            ActionButton = (
                <button className="action-btn btn-secondary" disabled>
                    Request Sent
                </button>
            );
        } else {
            ActionButton = (
                <button className="action-btn btn-primary" onClick={() => handleAccept()} disabled={isAccepting}>
                    Accept Invite
                </button>
            );
        }
    } else if (connection.status === 'rejected') {
         ActionButton = (
            <button className="action-btn btn-secondary" disabled>
                Request Rejected
            </button>
        );
    }
  } else {
    ActionButton = (
        <button className="action-btn btn-primary" onClick={() => handleRequest()} disabled={isRequesting}>
            Request Tutor
        </button>
    );
  }

  return createPortal(
    <div className="public-profile-wrapper">
      <div className="public-profile-card">
        {/* Banner */}
        <div className="profile-banner">
          {backgroundUrl && (
            <img src={backgroundUrl} alt="Cover" className="profile-banner-img" />
          )}
        </div>

        {/* Header */}
        <header className="profile-header">
          <div className="profile-avatar-wrapper">
            <img src={avatarUrl} alt={tutor.full_name} className="profile-avatar-img" />
            <div className="profile-status-badge" title="Active"></div>
          </div>
          
          <div className="header-actions">
            <button className="action-btn btn-secondary" onClick={handleBack}>
               Back
            </button>
            {ActionButton}
          </div>
        </header>

        {/* Body */}
        <div className="profile-body">
          {requestMessage.text && <Message text={requestMessage.text} type={requestMessage.type} />}

          <div className="user-identity">
            <h2>
              {tutor.display_name || tutor.full_name}
              <svg className="badge-verified" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                 <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                 <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </h2>
            <div className="user-headline">{tutor.headline || "Language Tutor"}</div>
          </div>

          <div className="info-grid">
             <div className="info-item">
                <div className="info-label">Language</div>
                <div className="info-value" style={{textTransform: 'uppercase'}}>{tutor.language?.replace('_', '-')}</div>
             </div>
             <div className="info-item">
                <div className="info-label">Experience</div>
                <div className="info-value">{tutor.years_of_experience} Years</div>
             </div>
          </div>

          <div className="profile-section">
            <h4 className="section-title">About Me</h4>
            <div className="bio-text">
                {tutor.bio || tutor.self_introduction || "No introduction provided."}
            </div>
          </div>

          {workHistory.length > 0 && (
            <div className="profile-section">
              <h4 className="section-title">Work History</h4>
              <div className="work-history-list">
                {workHistory.map((job) => (
                  <div key={job.id || Math.random()} className={`history-item ${job.isCurrent ? 'is-current' : ''}`}>
                    <div className="history-role">{job.role}</div>
                    <div className="history-company">{job.company}</div>
                    <div className="history-date">
                      {job.startDate} - {job.isCurrent ? 'Present' : job.endDate}
                    </div>
                    {job.description && (
                      <div className="history-description">{job.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
